import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableExpandedRows,
  DataTableRowEvent,
  DataTableStateEvent,
  DataTableValueArray,
} from 'primereact/datatable';
import { classNames } from 'primereact/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EmptyScreen from '../../../../../../../../components/empty-screen/EmptyScreen';
import { useGetPaginatedComponentTasks } from '../../../../../../../../hooks/api/request-pcf';
import { useComponentTasksTableQueryParams } from '../../../../../../../../hooks/useComponentTasksTableQueryParams';
import { Locale } from '../../../../../../../../i18n';
import { COMPONENT_TASKS_COMPLETE_STATUS_MAPPING } from '../../../../../../../../shared/constants/tasks.constant';
import { PCFRequestStatus } from '../../../../../../../../shared/enums/pcf-request';
import { getPaginatorTemplate } from '../../../../../../../../shared/helpers/get-paginator-template';
import { parseDateWithoutTime } from '../../../../../../../../shared/helpers/parse-date';
import { IRequest } from '../../../../../../../../shared/interfaces/IRequest';
import TaskStatus from '../../../../../task-status/TaskStatus';
import ProductComponentId from '../../../components/product-component-id/ProductComponentId';
import ProductComponentName from '../../../components/product-component-name/ProductComponentName';
import ComponentTasksTableHeader from '../component-tasks-table-header/ComponentTasksTableHeader';
import styles from './ComponentTasksTable.module.css';

const ComponentTasksTable = (): JSX.Element => {
  const { setParams, skip, size, sort, filters } = useComponentTasksTableQueryParams();
  const [sortField, sortOrder] = sort ? sort.split('-') : [undefined, undefined];
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const {
    i18n: { language },
    t,
  } = useTranslation();

  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  const { data, isLoading } = useGetPaginatedComponentTasks({
    skip,
    size,
    sort,
    ...filters,
  });

  useEffect(() => {
    if (data) {
      setTotalRecords(data?.pagination?.total || 0);
    }
  }, [data]);

  const componentNameHeaderTemplate = (): JSX.Element => {
    return (
      <div className='flex gap-1 flex-column'>
        <span>
          {t('tasksPage.tabs.componentTasksTab.componentTasksTableColumns.componentName')}
        </span>
        <span className='text-sm font-light text-gray-700 font-italic'>
          {t('tasksPage.tabs.componentTasksTab.componentTasksTableColumns.yourName')}
        </span>
      </div>
    );
  };

  const componentIdHeaderTemplate = (): JSX.Element => {
    return (
      <div className='flex gap-1 flex-column'>
        <span>{t('tasksPage.tabs.componentTasksTab.componentTasksTableColumns.componentId')}</span>
        <span className='text-sm font-light text-gray-700 font-italic'>
          {t('tasksPage.tabs.componentTasksTab.componentTasksTableColumns.yourId')}
        </span>
      </div>
    );
  };

  const componentNameTemplate = (rowData: IRequest): JSX.Element => {
    let componentName;
    let requestedComponentName;

    // Case 1: If it is additional component request and has been answered, stack both names
    if (
      rowData.product &&
      rowData.otherRequestData &&
      rowData.status === PCFRequestStatus.Complete
    ) {
      componentName = rowData.product.productName;
      requestedComponentName = rowData.otherRequestData.component_name;
    }
    // Case 2: If it is additional component request and hasn't been answered, show the requested name as main name
    else if (rowData.otherRequestData) {
      componentName = rowData.otherRequestData.component_name;
    }
    // Case 3: If it is not additional component request, show the product name as main name
    else {
      componentName = rowData?.product?.productName;
    }

    return <ProductComponentName name={componentName} requestedName={requestedComponentName} />;
  };

  const componentIdTemplate = (rowData: IRequest): JSX.Element => {
    let componentId;
    let requestedComponentId;

    // Case 1: If it is additional component request and has been answered, stack both names
    if (
      rowData.product &&
      rowData.otherRequestData &&
      rowData.status === PCFRequestStatus.Complete
    ) {
      componentId = rowData.product.productId;
      requestedComponentId = rowData.otherRequestData.component_id;
    }
    // Case 2: If it is additional component request and hasn't been answered, show the requested name as main name
    else if (rowData.otherRequestData) {
      componentId = rowData.otherRequestData.component_id;
    }
    // Case 3: If it is not additional component request, show the product name as main name
    else {
      componentId = rowData?.product?.productId;
    }

    return <ProductComponentId id={componentId} requestedId={requestedComponentId} />;
  };

  const supplierNameTemplate = (rowData: IRequest): JSX.Element => {
    return <span>{rowData.company?.companyName ?? rowData.company?.companyBpn}</span>;
  };

  const requestedByTemplate = (rowData: IRequest): JSX.Element => {
    return <span>{`${rowData.user?.firstName} ${rowData.user?.lastName}`}</span>;
  };

  const lastUpdatedTemplate = (rowData: IRequest): React.ReactNode => {
    // Determine lastUpdated date based on several conditions
    let lastUpdated;

    if (COMPONENT_TASKS_COMPLETE_STATUS_MAPPING.includes(rowData.status)) {
      lastUpdated = rowData.dateAnswered;
    } else {
      lastUpdated = rowData.dateStatusChanged;
    }

    // Fallback to dateCreated if lastUpdated is undefined
    return parseDateWithoutTime(lastUpdated || rowData.dateCreated, language as Locale);
  };

  const statusTemplate = (rowData: IRequest): JSX.Element => {
    return <TaskStatus status={rowData.status} taskType='component' />;
  };

  const rowExpansionTemplate = (data: IRequest): JSX.Element => {
    return (
      <div
        className={classNames(
          styles['comment-expansion-container'],
          'flex flex-column w-full gap-3',
        )}
      >
        {/* Comment */}
        <div className='flex flex-column gap-1 '>
          <span>{t('global.comment')}</span>
          <span>{data.message}</span>
        </div>

        {/* Supplier Email */}
        {data.otherRequestData?.supplier?.email && (
          <div className='flex flex-column gap-1'>
            <span>{t('global.sentTo')}</span>
            <span>{data.otherRequestData?.supplier?.email}</span>
          </div>
        )}
      </div>
    );
  };

  const onRowToggle = (event: DataTableRowEvent): void => {
    setExpandedRows(event.data);
  };

  const allowExpansion = (rowData: IRequest) => {
    return !!rowData.message;
  };

  const isTableEmpty =
    !isLoading &&
    data?.data.length === 0 &&
    Object.values(filters).filter((value) => {
      return Boolean(value);
    }).length === 0;

  if (isTableEmpty) {
    return (
      <EmptyScreen>
        <p className='flex font-light text-center'>
          {t('tasksPage.tabs.componentTasksTab.emptyScreenText')}
        </p>
      </EmptyScreen>
    );
  }

  return (
    <div className={styles['component-tasks-table']}>
      <DataTable
        value={data?.data}
        scrollable
        rowExpansionTemplate={rowExpansionTemplate}
        expandedRows={expandedRows}
        onRowToggle={onRowToggle}
        loading={isLoading}
        header={<ComponentTasksTableHeader />}
        rows={size}
        paginatorPosition='top'
        paginator
        sortField={sortField}
        sortOrder={sortField ? (sortOrder === 'desc' ? -1 : 1) : undefined}
        onSort={(event: DataTableStateEvent) => {
          const newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
          setParams({
            sort: `${event.sortField}-${newSortOrder}`,
            page: 1,
          });
        }}
        paginatorTemplate={getPaginatorTemplate({
          totalRecords,
          rowsPerPage: size,
          skip,
          onNext: () => {
            setParams({ page: (skip + size) / size + 1 });
          },
          onPrev: () => {
            setParams({ page: (skip + size) / size - 1 });
          },
        })}
        lazy
        tableStyle={{
          tableLayout: 'fixed',
          minWidth: '1200px',
        }}
        emptyMessage={t('global.emptyTableMessage')}
      >
        <Column
          expander={allowExpansion}
          headerStyle={{ width: '60px', maxWidth: '60px' }}
          style={{ width: '60px', maxWidth: '60px' }}
        />

        <Column
          header={componentNameHeaderTemplate}
          body={componentNameTemplate}
          sortable
          sortField='componentName'
          style={{ width: '20%', wordBreak: 'break-word' }}
        />
        <Column
          header={componentIdHeaderTemplate}
          body={componentIdTemplate}
          sortable
          sortField='componentId'
          style={{ width: '15%', wordBreak: 'break-word' }}
        />

        <Column
          header={t('tasksPage.tabs.componentTasksTab.componentTasksTableColumns.supplierName')}
          body={supplierNameTemplate}
          sortable
          sortField='supplierName'
          style={{ wordBreak: 'break-word' }}
        />

        <Column
          header={t('tasksPage.tabs.componentTasksTab.componentTasksTableColumns.requestedBy')}
          body={requestedByTemplate}
          sortable
          sortField='requestedBy'
          style={{ wordBreak: 'break-word' }}
        />

        <Column
          header={t('tasksPage.tabs.componentTasksTab.componentTasksTableColumns.lastUpdated')}
          body={lastUpdatedTemplate}
          sortable
          sortField='lastUpdated'
        />

        <Column
          header={t('tasksPage.tabs.componentTasksTab.componentTasksTableColumns.status')}
          body={statusTemplate}
          sortable
          sortField='status'
        />
      </DataTable>
    </div>
  );
};

export default ComponentTasksTable;
